import styled from "@emotion/styled";
import {
  Link,
  IconTwitter,
  IconLinkedin,
  IconInstagram,
  IconWebsite,
  IconOpenSea,
  IconDiscord,
  IconTelegram,
  IconFoundation,
  IconMagicEden,
  IconMedium,
  IconReddit,
  IconTikTok,
  IconYouTube,
  IconEtherscan,
} from "atoms";
import { ReviewableLink } from "../types";

interface Props {
  name: string;
  link: ReviewableLink;
}

function fixUrl(link: string): string {
  if (link.startsWith("http")) {
    return link;
  }
  return `https://${link}`;
}
function removeHttp(link: string): string {
  return link.replace(/^https?:\/\//, "");
}
function removeWww(link: string): string {
  return link.replace(/^www\./, "");
}

function SocialLink({ name, link }: Props) {
  const url = fixUrl(link.url).replace(/\/+$/, "");
  const urlObject = new URL(url);
  const handle = urlObject.pathname.slice(1);
  const noHttp = removeHttp(url);
  const prettyUrl = removeWww(noHttp);
  switch (link.type) {
    case "etherscan":
      return (
        <StyledLink href={url} title={`${name} on Etherscan`}>
          <IconEtherscan aria-hidden="true" />
          <LinkText>Etherscan</LinkText>
        </StyledLink>
      );
    case "instagram":
      return (
        <StyledLink href={url} title={`${name} on Instagram`}>
          <IconInstagram aria-hidden="true" />
          <LinkText>@{handle}</LinkText>
        </StyledLink>
      );
    case "linkedin":
      return (
        <StyledLink href={url} title={`${name} on LinkedIn`}>
          <IconLinkedin aria-hidden="true" />
          <LinkText>LinkedIn</LinkText>
        </StyledLink>
      );
    case "openSea":
      return (
        <StyledLink href={url} title={`${name} on OpenSea`}>
          <IconOpenSea aria-hidden="true" />
          <LinkText>OpenSea</LinkText>
        </StyledLink>
      );
    case "twitter":
      return (
        <StyledLink href={url} title={`${name} on Twitter`}>
          <IconTwitter aria-hidden="true" />
          <LinkText>@{handle}</LinkText>
        </StyledLink>
      );
    case "website":
      return (
        <StyledLink href={url} title={`Link to the ${name} website`}>
          <IconWebsite aria-hidden="true" />
          <LinkText>{prettyUrl}</LinkText>
        </StyledLink>
      );
    case "discord":
      return (
        <StyledLink href={url} title={`${name} on Discord`}>
          <IconDiscord aria-hidden="true" />
          <LinkText>Discord</LinkText>
        </StyledLink>
      );
    case "telegram":
      return (
        <StyledLink href={url} title={`Link to the ${name} Telegram`}>
          <IconTelegram aria-hidden="true" />
          <LinkText>@{handle}</LinkText>
        </StyledLink>
      );
    case "foundation":
      return (
        <StyledLink href={url} title={`Link to the ${name} Foundation`}>
          <IconFoundation aria-hidden="true" />
          <LinkText>Foundation</LinkText>
        </StyledLink>
      );
    case "magicEden":
      return (
        <StyledLink href={url} title={`${name} on Magic Eden`}>
          <IconMagicEden aria-hidden="true" />
          <LinkText>Magic Eden</LinkText>
        </StyledLink>
      );
    case "medium":
      return (
        <StyledLink href={url} title={`${name} on Medium`}>
          <IconMedium aria-hidden="true" />
          <LinkText>Medium</LinkText>
        </StyledLink>
      );
    case "reddit":
      return (
        <StyledLink href={url} title={`${name} on Reddit`}>
          <IconReddit aria-hidden="true" />
          <LinkText>Reddit</LinkText>
        </StyledLink>
      );
    case "tiktok":
      return (
        <StyledLink href={url} title={`${name} on TikTok`}>
          <IconTikTok aria-hidden="true" />
          <LinkText>{handle}</LinkText>
        </StyledLink>
      );
    case "youtube":
      return (
        <StyledLink href={url} title={`${name} on YouTube`}>
          <IconYouTube aria-hidden="true" />
          <LinkText>YouTube</LinkText>
        </StyledLink>
      );
  }
  // Unknown Link Type
  return (
    <StyledLink href={url} title={`${link.type} Link to ${url}`}>
      <IconWebsite aria-hidden="true" />
      <LinkText>{prettyUrl}</LinkText>
    </StyledLink>
  );
}

const StyledLink = styled(Link)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flex: "0 0 auto",
  flexWrap: "nowrap",
  gap: ".33em",
  textDecoration: "none",
  maxWidth: "max-content",
  svg: {
    width: "1em",
    height: "auto",
    minWidth: "1em",
  },
}));

const LinkText = styled.span(({ theme }) => ({
  fontSize: theme.fontSizes.xxs,
  overflow: "hidden",
  padding: "2px 0",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
StyledLink.defaultProps = {
  target: "_blank",
  rel: "noreferrer",
};

export { SocialLink };
